/**
 *
 * ReverseTransactionAmountModal
 *
 * @version 1.0.0
 * @author Giuseppe
 * @since 18-Jan-2023
 */

import React from 'react';
import PortalModal from 'Components/PortalModal/PortalModal';
import PortalTextField from 'Components/PortalTextField';
import { Grid, Typography } from '@mui/material';
import formattedMessages from '../../modules/languageModule';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { formatAmount, amountToCents } from 'Utility/utils';
import { currency } from 'Utility/strings';
import style from './ReverseTransactionAmountModal.scss';

const reverseAmount = 'reverseAmount';
const inputFieldNumberType = 'number';
const formId = 'couponManagementForm';

const ReverseTransactionAmountModal = (props) => {
	const intl = useIntl();
	const transactionDetail = props.transactionItem.transactionDetail;
	const order = props.transactionItem.order;

	const maxAmount = transactionDetail.amount - order.refundedAmount;

	const formik = useFormik({
		initialValues: {
			[reverseAmount]: '',
		},
		validationSchema: Yup.object({
			[reverseAmount]: Yup.number()
				.required(intl.formatMessage(formattedMessages.fieldRequested))
				.max(
					maxAmount,
					intl.formatMessage(formattedMessages.maxAmount, {
						number: formatAmount(maxAmount, props.transactionItem.transactionDetail.currency),
					})
				),
		}),
		onSubmit: (values, { setSubmitting }) => {
			props.actions.setReverseAmount({
				username: props.transactionItem.order.username,
				groupId: transactionDetail.groupId,
				registerId: transactionDetail.registerId,
				transactionId: transactionDetail.transactionId,
				amount: values.reverseAmount * 100,
				currency: transactionDetail.currency,
			});
			setSubmitting(false);
		},
	});

	const checkAndDisableApplyButton = () => {
		const amount = amountToCents("" + formik.values[reverseAmount], props.transactionItem.transactionDetail.currency);
		return isNaN(amount) || amount <= 0 || amount > maxAmount;
	};

	return (
		<PortalModal
			className={style.container}
			title={formattedMessages.editAmount}
			actions={{ handleClose: () => {} }}
			buttons={[
				{
					variant: 'outlined',
					onClick: props.actions.closeModal,
					contentText: formattedMessages.abortLabelButton,
				},
				{
					variant: 'contained',
					onClick: () => formik.handleSubmit(),
					contentText: formattedMessages.confirmLabelButton,
					isLoading: false,
					isButtonDisabled: checkAndDisableApplyButton(),
				},
			]}>
			<Grid>
				<Typography variant='body1' align='left'>
					<FormattedMessage {...formattedMessages.order} />{' '}
					{currency.filter((a) => a.code === transactionDetail.currency)[0].symbol}{' '}
					{formatAmount(transactionDetail.amount, transactionDetail.currency)}
				</Typography>
			</Grid>
			<Grid>
				<Typography variant='body1' align='left'>
					<FormattedMessage {...formattedMessages.alreadyreversed} />{' '}
					{currency.filter((a) => a.code === order.currency)[0].symbol}{' '}
					{formatAmount(order.refundedAmount, order.currency)}
				</Typography>
			</Grid>
			<form autoComplete='off' onSubmit={formik.handleSubmit} onReset={formik.handleReset} id={formId}>
				<PortalTextField
					name={reverseAmount}
					type={inputFieldNumberType}
					label={intl.formatMessage(formattedMessages.amount)}
					{...formik.getFieldProps(reverseAmount)}
				/>
			</form>
		</PortalModal>
	);
};

ReverseTransactionAmountModal.propTypes = {
	actions: PropTypes.objectOf(PropTypes.func),
	transactionItem: PropTypes.object,
};

ReverseTransactionAmountModal.defaultProps = {
	// Add here some default propTypes values
};

export default ReverseTransactionAmountModal;
